<template>
  <loading-page
    v-if="loading"
    full
  />
  <div
    v-else-if="showIntro"
  >
    <live-encoder-intro>
      <custom-alert
        icon-name="Info"
        items-center
        class="mt-2"
      >
        {{ errorMessage }}
      </custom-alert>
    </live-encoder-intro>
  </div>
  <div
    v-else
    class="table-wrapper"
  >
    <div class="d-flex justify-content-between flex-wrap">
      <div class="jobs-main d-flex w-100">
        <b-button
          variant="bg-primary"
          :to="{ name: 'create-live-encoder' }"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create Live Job
        </b-button>
        <b-button
          variant="bg-white"
          :disabled="!isSelected"
          class="d-none d-lg-flex"
          @click="idClick(selected[0].name)"
        >
          Job Settings
        </b-button>
        <button-loading
          v-if="$can('delete', 'jobs', 'encoder')"
          variant="bg-white"
          :disabled="!isSelected || removingLiveJob || selected[0].status === 'Deleted'"
          :spinning="removingLiveJob"
          class="d-none d-lg-inline action-live-job-button"
          @click="onDeleteClick(selected[0].name, selected[0].livestreamProvider)"
        >
          Delete
        </button-loading>
        <b-button
          variant="bg-white"
          class="ml-auto mr-0"
          @click="onGetLiveJobs()"
        >
          <GjIcon
            class="icon-left"
            name="Refresh"
          />
          Refresh
        </b-button>
      </div>
    </div>
    <div
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <div class="position-relative d-none d-lg-block">
        <b-row v-if="!fetchingLiveJobStatuses">
          <b-col
            v-if="isFromLiveHub"
            class="pt-2 ml-2"
          >
            <b-dropdown
              id="jobs-filter-dropdown-1"
              :text="liveHubJobsStatusText"
              variant="bg-white"
              class="dropdown-icon-wrapper"
            >
              <template #button-content>
                <span>{{ liveHubJobsStatusText }}</span>
                <GjIcon
                  class="icon-right"
                  name="ArrowDown"
                />
              </template>
              <b-dropdown-item
                v-for="status in liveHubJobsStatusOptions"
                :key="status.id"
                @click="liveHubJobsStatus = status.id"
              >
                {{ status.value }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
          <b-col
            v-else
            class="pt-2 ml-2"
          >
            <b-dropdown
              id="jobs-filter-dropdown-1"
              :text="liveJobStatusText"
              variant="bg-white"
              class="dropdown-icon-wrapper"
            >
              <template #button-content>
                <span>{{ liveJobStatusText }}</span>
                <GjIcon
                  class="icon-right"
                  name="ArrowDown"
                />
              </template>
              <b-dropdown-item
                v-for="status in liveJobsStatusOptions"
                :key="status.id"
                @click="liveJobStatus = status.id"
              >
                {{ status.value }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>

          <b-col>
            <b-input-group
              v-b-tooltip.hover.v-primary
              class="searchInputGroup ml-auto mr-2 pt-2 mt-sm-0"
              title="Search by name or description"
            >
              <b-input-group-prepend is-text>
                <b-icon-search @click="searchInputChange" />
              </b-input-group-prepend>
              <b-form-input
                id="filterInput"
                v-model.trim="searchInput"
                type="search"
                class="searchInput"
                placeholder="Search..."
                @change="searchInputChange"
              />
            </b-input-group>
          </b-col>
        </b-row>
        <b-table
          ref="table"
          select-mode="single"
          :items="liveJobs"
          :fields="fields"
          :class="{ loaded: !loading && !updatingLiveHub }"
          show-empty
          selectable
          responsive
          @row-selected="onRowSelected"
        >
          <template
            #cell(selected)="{rowSelected}"
          >
            <select-button :selected="rowSelected" />
          </template>
          <template #empty>
            <div
              v-if="loading || updatingLiveHub"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <no-data
              v-else
              no-data-text="No live jobs have been added yet..."
              create-text="+ Create a live job"
              access-page="Live Jobs"
              icon="Section"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'create-live-encoder' })"
            />
          </template>
          <template #cell(name)="data">
            <router-link
              :to="{
                name: 'live-encoder-settings',
                params: { name: data.value },
              }"
            >
              {{ data.value }}
            </router-link>
          </template>
          <template #cell(insertDate)="data">
            {{ formatDateTime(data.value) }}
          </template>
          <template #cell(startDateTime)="data">
            {{ formatDateTime(data.value) }}
          </template>
          <template #cell(endDateTime)="data">
            {{ formatDateTime(data.value) }}
          </template>
          <template #cell(isActive)="data">
            <b-button
              v-if="liveHub.activeLiveJobId === data.item.id"
              variant="bg-white"
              disabled
            >
              Active
            </b-button>
            <b-button
              v-else
              variant="bg-primary"
              @click="setActiveLiveJob(data.item)"
            >
              Set Active
            </b-button>
          </template>
        </b-table>
        <table-busy v-if="(loading || updatingLiveHub) && liveJobs.length > 0" />
      </div>

      <!-- MOBILE -->
      <div class="position-relative d-lg-none mt-1">
        <div
          :class="[{ 'opacity-7': loading || updatingLiveHub}, {'shadow': isFromLiveHub }]"
        >
          <template v-for="item in liveJobs">
            <card
              :key="item.name"
              :fields="fields"
              :item="item"
              class="top-fade-in"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item @click="idClick(item.name)">
                    Job Settings
                  </b-dropdown-item>
                </b-dropdown>
              </template>

              <template #name>
                <router-link
                  :to="{
                    name: 'live-encoder-settings',
                    params: { name: item.name },
                  }"
                >
                  {{ item.name }}
                </router-link>
              </template>

              <template #insertDate>
                {{ formatDateTime(item.insertDate) }}
              </template>
              <template #startDateTime>
                {{ formatDateTime(item.startDateTime) }}
              </template>
              <template #endDateTime>
                {{ formatDateTime(item.endDateTime) }}
              </template>
              <template #isActive>
                <b-button
                  v-if="liveHub.activeLiveHubId === item.id"
                  variant="bg-primary"
                  disabled
                >
                  Active
                </b-button>
                <b-button
                  v-else
                  variant="bg-primary"
                  @click="setActiveLiveJob(item)"
                >
                  Set Active
                </b-button>
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="loading || updatingLiveHub"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="(!loading && !updatingLiveHub) && liveJobs.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No live jobs have been added yet..."
              create-text="+ Create a live job"
              access-page="Live Jobs"
              icon="Section"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'create-live-encoder' })"
            />
          </paper-simple>
        </template>
      </div>
      <div
        v-if="liveJobs.length > 0"
        class="default-pagination"
        :class="{'mt-2': isFromLiveHub}"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import LiveEncoderIntro from './LiveEncoderIntro.vue'
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'LiveEncoder',
  components: { Card, BIconSearch, LiveEncoderIntro },
  props: {
    isFromLiveHub: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchInput: '',
      liveJobStatus: -1,
      liveHubJobsStatus: 1,
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'name',
          variant: 'guid',
          isPrimary: true,
        },
        { key: 'status', variant: 'small', isPrimary: true },
        { key: 'insertDate', variant: 'time' },
        { key: 'startDateTime', label: 'Start Date', variant: 'time' },
        { key: 'endDateTime', label: 'End Date', variant: 'time' },
        { key: 'description', label: 'Description' },
      ],
      selected: [],
      isSelectedPristine: true,
      hasPermissions: true,
      showIntro: false,
      errorMessage: '',
    }
  },
  computed: {
    fetchingLiveJobStatuses() {
      return this.$store.state.liveEncoder.fetchingLiveJobStatuses
    },
    removingLiveJob() {
      return this.$store.state.liveEncoder.removingLiveJob
    },
    liveJobsStatusOptions() {
      return this.$store.state.liveEncoder.liveJobStatuses || []
    },
    liveHubJobsStatusOptions() {
      return this.$store.state.liveEncoder.liveJobStatuses.filter(element => element.id === 1 || element.id === 2) || []
    },
    liveJobStatusText() {
      return (
        this.liveJobsStatusOptions.find(j => j.id === this.liveJobStatus)
          ?.value || ''
      )
    },
    liveHubJobsStatusText() {
      return (
        this.liveHubJobsStatusOptions.find(j => j.id === this.liveHubJobsStatus)
          ?.value || ''
      )
    },
    isSelected() {
      return this.selected[0]
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    updatingLiveHub() {
      return this.$store.state.liveHubs.updatingLiveHub
    },
    loading() {
      return this.$store.state.liveEncoder.fetchingLiveJobs
    },
    liveJobs() {
      return this.$store.state.liveEncoder.liveJobs
    },
    liveHub() {
      return this.$store.state.liveHubs.liveHub
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    resultInfo() {
      return this.$store.state.liveEncoder.liveJobsResultInfo
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },

    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentPage: {
      get() {
        return this.$store.getters['liveEncoder/currentPage']
      },
      set(val) {
        this.$store.commit('liveEncoder/SET_CURRENT_PAGE', val)
      },
    },
    selectedLiveJobs: {
      get() {
        return this.$store.state.liveHubs.selectedLiveJobs
      },
      set(val) {
        this.$store.commit('liveHubs/SET_SELECTED_LIVE_JOBS', val)
      },
    },
  },
  watch: {
    currentPage() {
      this.onGetLiveJobs()
    },
    liveJobStatus() {
      if (this.currentPage === 1) {
        this.onGetLiveJobs()
      } else {
        this.currentPage = 1
      }
    },
    liveHubJobsStatus() {
      if (this.currentPage === 1) {
        this.onGetLiveJobs()
      } else {
        this.currentPage = 1
      }
    },
  },
  created() {
    if (this.$store.state.app.previousRouteName !== 'live-encoder-settings') {
      this.currentPage = 1
    }
    if (this.isFromLiveHub) {
      this.fields.push({ key: 'isActive', label: 'Is Active' })
    }
    this.onGetLiveJobs()
    this.fetchLiveJobsStatuses()
  },
  methods: {
    onRowSelected(item) {
      this.selected = item
    },
    searchInputChange() {
      if (this.currentPage === 1) {
        this.onGetLiveJobs()
      } else {
        this.currentPage = 1
      }
    },
    idClick(id) {
      this.$router.push({ name: 'live-encoder-settings', params: { name: id } })
    },
    onGetLiveJobs() {
      this.$store
        .dispatch('liveEncoder/fetchLiveJobsV2', {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          status: this.isFromLiveHub ? this.liveHubJobsStatus : this.liveJobStatus,
          search: this.searchInput,
          livestreamProvider: this.isFromLiveHub ? this.liveHub.livestreamProvider : null,
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          if (error.response?.status === 404) {
            this.errorMessage = error.response?.data?.errors[0]
            this.showIntro = true
            return
          }
          this.errorToast(error.response?.data?.errors || 'Something went wrong while fetching live jobs.')
        })
    },
    onDeleteClick(liveJobId, livestreamProvider) {
      const version = livestreamProvider === 1 ? '' : 'V2'
      this.confirmDelete(
        'Delete Live Job',
        'Are you sure you want to delete this live job?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('liveEncoder/removeLiveJob', { liveJobId, version })
            .then(() => {
              this.successToast('Live Job was removed')
              this.selected = []
              if (this.liveJobs.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.onGetLiveJobs()
              }
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting live job.')
            })
        }
      })
    },
    fetchLiveJobsStatuses() {
      this.$store.dispatch('liveEncoder/fetchLiveJobsStatuses').catch(() => {
        this.errorToast('Something went wrong')
      })
    },
    setActiveLiveJob(liveJob) {
      const updateLiveHubStatus = {
        liveHubName: this.liveHub.name,
        activeLiveJobName: liveJob.name,
        version: this.liveHub.livestreamProvider === 1 ? '' : 'V2',
      }
      this.updateLiveHubStatus(updateLiveHubStatus)
    },
    updateLiveHubStatus(updateModel) {
      this.$store
        .dispatch('liveHubs/updateLiveHubStatus', updateModel)
        .then(() => {
          this.isSelectedPristine = true
          this.$emit('onGetLiveJobs')
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating live hub.')
        })
    },
  },
}
</script>
<style scoped>
.searchInputGroup {
  width: 250px;
}
.searchInput {
  margin-right: 0;
}
</style>
